import type { ChangeEvent, FC } from "react";
import React from "react";
import { useSelector } from "react-redux";
import {
  getIsPasswordInvalid,
  getIsUserLoading,
  getIsVerifyPasswordInvalid,
  getNewPassword,
  getResourceOwner,
  getTunnistusEmail,
  getVerifyPassword,
} from "../../store/auth/authGetters";
import { setPassword, setVerifyPassword } from "../../store/auth/authSlice";
import { passwordUpdate } from "../../store/auth";
import { useAppDispatch } from "../../store/configureStore";
import { Redirect } from "react-router-dom";
import { routes } from "../../utils/routes";
import { Button, Margin } from "@kivra/react-components";
import { ButtonContainer } from "../layouts/ButtonContainer";
import { useCopy } from "../../utils/globalContext";
import { getIsPasswordResetDisabled } from "../../store/signup";
import type { RootState } from "../../store/rootReducer";
import { KivraHeaderRouter } from "../header/KivraHeaderRouter";
import { TextFieldWithOnBlurValidation } from "./TextFieldWithOnBlurValidation";
import { PresetFormValue } from "./PresetFormValue";

const PasswordResetForm: FC = () => {
  const copy = useCopy();
  const {
    emailAddress,
    password,
    verifyPassword,
    isLoading,
    isPasswordInvalid,
    isVerifyPasswordInvalid,
    isPasswordResetDisabled,
    isAuthenticated,
  } = useSelector((state: RootState) => ({
    emailAddress: getTunnistusEmail(state),
    password: getNewPassword(state),
    verifyPassword: getVerifyPassword(state),
    isLoading: getIsUserLoading(state),
    isPasswordInvalid: getIsPasswordInvalid(state),
    isVerifyPasswordInvalid: getIsVerifyPasswordInvalid(state),
    isPasswordResetDisabled: getIsPasswordResetDisabled(state),
    isAuthenticated: getResourceOwner(state),
  }));
  const { setPasswordValue, setVerifyPasswordValue, updatePasswordRequest } =
    usePasswordUpdateActions();
  if (isAuthenticated.length < 1) {
    return (
      <>
        <Redirect to={routes.ROOT} />
      </>
    );
  } else {
    return (
      <>
        <KivraHeaderRouter />
        <PresetFormValue
          label={copy("input_placeholder__email")}
          value={emailAddress}
        />
        <Margin bottom={20} />
        <TextFieldWithOnBlurValidation
          dataTestId="new-password-input"
          label={copy("input_placeholder__password__new")}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPasswordValue}
          autoFocus
          isInvalid={isPasswordInvalid}
          hintText={copy("input_hint__password__register")}
        />
        <Margin bottom={20} />
        <TextFieldWithOnBlurValidation
          dataTestId="verify-password-input"
          label={copy("input_placeholder__password__repeat")}
          type="password"
          autoComplete="new-password"
          value={verifyPassword}
          onChange={setVerifyPasswordValue}
          isInvalid={isVerifyPasswordInvalid}
          hintText={copy("input_hint__password_repeat__register")}
        />
        <ButtonContainer>
          <Button
            progress={isLoading}
            disabled={isPasswordResetDisabled}
            onClick={(event) => {
              event.preventDefault();
              updatePasswordRequest();
            }}
            type="submit"
            size="medium"
          >
            {copy("btn_reset_password")}
          </Button>
        </ButtonContainer>
      </>
    );
  }
};

export default PasswordResetForm;

const usePasswordUpdateActions = () => {
  const dispatch = useAppDispatch();
  const setPasswordValue = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setPassword(event.target.value));
  };
  const setVerifyPasswordValue = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setVerifyPassword(event.target.value));
  };
  const updatePasswordRequest = () => dispatch(passwordUpdate());
  return { setPasswordValue, setVerifyPasswordValue, updatePasswordRequest };
};
